// packages/components/src/agent_profile/showcase/ValuePropSimple.tsx
import React from 'react';
import { Card, Button, Badge, Tooltip } from '@dopomogai/ui';
import { Lightbulb, Sparkles, ListChecks, Users, CheckCircle2 } from 'lucide-react';

type Accent =
  | 'emerald'
  | 'teal'
  | 'amber'
  | 'sky'
  | 'violet'
  | 'indigo';

type Props = {
  title?: string; // defaults to "Why this agent"
  whyText?: string; // 1–3 short paragraphs
  benefits?: string[]; // 3–6 concise bullets
  useCases?: string[]; // 3–6 concise bullets
  audienceText?: string; // 1 paragraph
  primaryCta?: { text: string; onClick: () => void };
  className?: string;

  // New: colorized header option
  headerAccent?: Accent; // e.g., 'emerald' | 'teal' | 'sky' | ...
};

function accentToClasses(accent?: Accent) {
  switch (accent) {
    case 'emerald':
      return { bg: 'bg-emerald-50', text: 'text-emerald-900', icon: 'text-emerald-700' };
    case 'teal':
      return { bg: 'bg-teal-50', text: 'text-teal-900', icon: 'text-teal-700' };
    case 'amber':
      return { bg: 'bg-amber-50', text: 'text-amber-900', icon: 'text-amber-700' };
    case 'sky':
      return { bg: 'bg-sky-50', text: 'text-sky-900', icon: 'text-sky-700' };
    case 'violet':
      return { bg: 'bg-violet-50', text: 'text-violet-900', icon: 'text-violet-700' };
    case 'indigo':
      return { bg: 'bg-indigo-50', text: 'text-indigo-900', icon: 'text-indigo-700' };
    default:
      return { bg: 'bg-slate-50', text: 'text-slate-900', icon: 'text-slate-700' };
  }
}

export function ValuePropSimple({
  title = 'Why this agent',
  whyText,
  benefits = [],
  useCases = [],
  audienceText,
  primaryCta,
  className,
  headerAccent,
}: Props) {
  const hasTop = Boolean(whyText);
  const hasBenefits = benefits.length > 0;
  const hasUseCases = useCases.length > 0;
  const hasAudience = Boolean(audienceText);
  const hasAnything = hasTop || hasBenefits || hasUseCases || hasAudience;

  if (!hasAnything) return null;

  // If headerAccent provided, render our own accented header strip; else fall back to Card's default header.
  if (headerAccent) {
    const c = accentToClasses(headerAccent);
    return (
      <Card className={className} noPadding>
        <div className={['px-5 py-4 border-b-2 border-black rounded-t-xl', c.bg, c.text].join(' ')}>
          <div className="flex items-center gap-2">
            <Lightbulb size={22} className={c.icon} />
            <h3 className="text-xl font-extrabold leading-tight">{title}</h3>
          </div>
        </div>
        <div className="p-5">
          <div className="space-y-10 md:space-y-12">
            {hasTop && (
              <section aria-labelledby="vp-why">
                <div
                  id="vp-why"
                  className="inline-flex items-center gap-2.5 bg-emerald-50 px-3 py-1.5 rounded-full border-2 border-black text-emerald-800 mb-3"
                >
                  <Lightbulb size={18} />
                  <span className="text-sm font-bold">Why it matters</span>
                </div>
                <p className="text-slate-700 text-lg leading-relaxed">{whyText}</p>
              </section>
            )}

            {hasBenefits && (
              <section aria-labelledby="vp-benefits" className="space-y-4">
                <div
                  id="vp-benefits"
                  className="inline-flex items-center gap-2.5 bg-teal-50 px-3 py-1.5 rounded-full border-2 border-black text-teal-800"
                >
                  <Sparkles size={18} />
                  <span className="text-sm font-bold">Key benefits</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {benefits.map((b, i) => (
                    <div
                      key={`benefit-${i}`}
                      className="bg-white border-2 border-black rounded-xl p-4 flex items-start gap-3"
                    >
                      <div className="p-1.5 bg-emerald-100 border-2 border-black rounded-md text-emerald-700">
                        <CheckCircle2 size={16} />
                      </div>
                      <p className="text-slate-800 font-medium leading-relaxed">{b}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {hasUseCases && (
              <section aria-labelledby="vp-usecases" className="space-y-4">
                <div
                  id="vp-usecases"
                  className="inline-flex items-center gap-2.5 bg-sky-50 px-3 py-1.5 rounded-full border-2 border-black text-sky-800"
                >
                  <ListChecks size={18} />
                  <span className="text-sm font-bold">Primary use cases</span>
                </div>
                <div className="space-y-3">
                  {useCases.map((u, i) => (
                    <div
                      key={`usecase-${i}`}
                      className="bg-white border-2 border-black rounded-xl p-3 text-sm flex items-start gap-3"
                    >
                      <Badge text={`${i + 1}`} accentColor="blue" />
                      <p className="text-slate-800">{u}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {hasAudience && (
              <section aria-labelledby="vp-audience" className="space-y-3">
                <div
                  id="vp-audience"
                  className="inline-flex items-center gap-2.5 bg-indigo-50 px-3 py-1.5 rounded-full border-2 border-black text-indigo-800"
                >
                  <Users size={18} />
                  <span className="text-sm font-bold">Who this is for</span>
                </div>
                <div className="bg-indigo-50 border-2 border-black rounded-xl p-4">
                  <p className="text-indigo-900">{audienceText}</p>
                </div>
              </section>
            )}

            {primaryCta && (
              <div className="pt-2">
                <Tooltip text="Personalize and get a quick quote">
                  <Button variant="primary" size="lg" onClick={primaryCta.onClick} text={primaryCta.text} />
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      </Card>
    );
  }

  // Default (non-accented) header usage (back-compat)
  return (
    <Card className={className} title={title} icon={<Lightbulb size={22} className="text-emerald-600" />}>
      <div className="space-y-10 md:space-y-12">
        {hasTop && (
          <section aria-labelledby="vp-why">
            <div id="vp-why" className="inline-flex items-center gap-2.5 bg-emerald-50 px-3 py-1.5 rounded-full border-2 border-black text-emerald-800 mb-3">
              <Lightbulb size={18} />
              <span className="text-sm font-bold">Why it matters</span>
            </div>
            <p className="text-slate-700 text-lg leading-relaxed">{whyText}</p>
          </section>
        )}

        {hasBenefits && (
          <section aria-labelledby="vp-benefits" className="space-y-4">
            <div id="vp-benefits" className="inline-flex items-center gap-2.5 bg-teal-50 px-3 py-1.5 rounded-full border-2 border-black text-teal-800">
              <Sparkles size={18} />
              <span className="text-sm font-bold">Key benefits</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div
                  key={`benefit-${i}`}
                  className="bg-white border-2 border-black rounded-xl p-4 flex items-start gap-3"
                >
                  <div className="p-1.5 bg-emerald-100 border-2 border-black rounded-md text-emerald-700">
                    <CheckCircle2 size={16} />
                  </div>
                  <p className="text-slate-800 font-medium leading-relaxed">{b}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {hasUseCases && (
          <section aria-labelledby="vp-usecases" className="space-y-4">
            <div id="vp-usecases" className="inline-flex items-center gap-2.5 bg-sky-50 px-3 py-1.5 rounded-full border-2 border-black text-sky-800">
              <ListChecks size={18} />
              <span className="text-sm font-bold">Primary use cases</span>
            </div>
            <div className="space-y-3">
              {useCases.map((u, i) => (
                <div
                  key={`usecase-${i}`}
                  className="bg-white border-2 border-black rounded-xl p-3 text-sm flex items-start gap-3"
                >
                  <Badge text={`${i + 1}`} accentColor="blue" />
                  <p className="text-slate-800">{u}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {hasAudience && (
          <section aria-labelledby="vp-audience" className="space-y-3">
            <div id="vp-audience" className="inline-flex items-center gap-2.5 bg-indigo-50 px-3 py-1.5 rounded-full border-2 border-black text-indigo-800">
              <Users size={18} />
              <span className="text-sm font-bold">Who this is for</span>
            </div>
            <div className="bg-indigo-50 border-2 border-black rounded-xl p-4">
              <p className="text-indigo-900">{audienceText}</p>
            </div>
          </section>
        )}

        {primaryCta && (
          <div className="pt-2">
            <Tooltip text="Personalize and get a quick quote">
              <Button variant="primary" size="lg" onClick={primaryCta.onClick} text={primaryCta.text} />
            </Tooltip>
          </div>
        )}
      </div>
    </Card>
  );
}

export default ValuePropSimple;
